import React from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import StyleOutlinedIcon from "@mui/icons-material/StyleOutlined";
import LinkIcon from "@mui/icons-material/Link";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import LibraryFilterChips from "./LibraryFilterChips";
import MoveAssetConfirm, { DESTINATIONS } from "./MoveAssetConfirm";
import { TrainingEmptyState } from "./TrainingEmptyState";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import LoadSpinner from "../../../utilities/LoadSpinner";

import {
  API_get_learning_swipe_deck_summaries,
  API_delete_learning_swipe_deck,
  API_post_set_learning_asset_purpose,
} from "../../../constants";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import { formatDate } from "../requests/utilities/helpers";

const ASSIGNABLE_OPTIONS = [
  { value: "assignable", label: "Assignable" },
  { value: "not_assignable", label: "Not assignable" },
];

// Keyed on a LIVE link, matching the Forms list: "Shared" answers "is this
// link open right now", not "was this ever shared".
const SHARING_OPTIONS = [
  { value: "shared", label: "Shared" },
  { value: "not_shared", label: "Not shared" },
];

const filterSelectSx = {
  height: 42,
  minWidth: 170,
  borderRadius: "8px",
  backgroundColor: "white",
  fontSize: 14,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  "& .MuiSelect-select": {
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
  },
};

/**
 * Visual card grid for the company's swipe decks.
 *
 * Mirrors the card-grid treatment of TracksDisplay rather than the
 * tabular DataGrid pattern used by QuizzesDisplay — decks are visual,
 * cover-driven content, and admins benefit from scanning a thumbnail
 * library rather than a dense table.
 *
 * Owns its own data fetch + search state. Editing flows through
 * `onEdit(deck)` / `onCreate()` callbacks so the parent
 * (CreateTrainingContentDisplay) can manage the editor modal alongside
 * the other entity modals it already coordinates.
 */
// Engagement decks are the same asset shared by public link instead of
// embedded in a course; only the copy and the assignability filter change.
const BLURB = {
  training: "Reusable card decks for fast, dynamic learning",
  engagement: "Quick swipeable updates and polls, shared by link or QR code",
};

function SwipeDecksDisplay(props) {
  const { homeCompany, onCreate, onEdit, onPreview, onChanged } = props;
  const purpose = props.purpose === "engagement" ? "engagement" : "training";
  const isEngagement = purpose === "engagement";
  const [movingUuid, setMovingUuid] = React.useState(null);
  // Every move confirms and names the destination tab (the deck vanishes from
  // this list).
  const [pendingMove, setPendingMove] = React.useState(null); // deck

  const [decks, setDecks] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [searchInput, setSearchInput] = React.useState("");
  const [assignableFilter, setAssignableFilter] = React.useState("all");
  const [sharingFilter, setSharingFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("recent");
  const [pendingDelete, setPendingDelete] = React.useState(null);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Data fetch

  const fetchDecks = React.useCallback(async () => {
    if (!homeCompany) return;
    setIsLoading(true);
    try {
      const res = await getRightworkAPI(
        API_get_learning_swipe_deck_summaries,
        { company_id: homeCompany, purpose },
      );
      const list = res?.data?.redux?.payload?.learning_swipe_decks || [];
      setDecks(list);
    } catch (err) {
      console.error("Failed to load swipe decks", err);
    } finally {
      setIsLoading(false);
    }
  }, [homeCompany, purpose]);

  // Move between the Training and Engagement tabs. The server refuses a move
  // into Engagement while the deck is still embedded in a section or directly
  // assignable, and says which sections.
  const moveDeck = async (deck) => {
    if (movingUuid) return;
    setPendingMove(null);
    setMovingUuid(deck.uuid);
    try {
      await postRightworkAPI(API_post_set_learning_asset_purpose, {
        company_id: homeCompany,
        asset_type: "swipe_deck",
        asset_uuid: deck.uuid,
        purpose: isEngagement ? "training" : "engagement",
      });
      toast.success(
        `Moved to ${DESTINATIONS[isEngagement ? "training" : "engagement"].swipe_deck}.`,
        { autoClose: 2500 }
      );
      if (onChanged) onChanged();
      else fetchDecks();
    } catch (err) {
      const message = err?.response?.data?.system?.message;
      toast.error(message || "Couldn't move this deck. Please try again.");
    } finally {
      setMovingUuid(null);
    }
  };

  React.useEffect(() => {
    fetchDecks();
  }, [fetchDecks]);

  // The parent forces a refresh after save/delete by changing the `key`
  // prop on this component, which remounts and re-fires the fetch. Simpler
  // than imperative refs and matches how other dashboards in this surface
  // sync with their editor modals.

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Filter

  const filteredDecks = React.useMemo(() => {
    const parts = searchInput.toLowerCase().split(" ").filter(Boolean);
    const filtered = decks.filter((deck) => {
      const haystack = `${deck.name || ""} ${deck.description || ""}`.toLowerCase();
      if (!parts.every((p) => haystack.includes(p))) return false;
      if (assignableFilter === "assignable" && !deck.is_directly_assignable) {
        return false;
      }
      if (assignableFilter === "not_assignable" && deck.is_directly_assignable) {
        return false;
      }
      if (sharingFilter === "shared" && !deck.public_is_shared) {
        return false;
      }
      if (sharingFilter === "not_shared" && deck.public_is_shared) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (sortKey === "cards") {
        return (b.cards_count || 0) - (a.cards_count || 0);
      }
      return (b.updated_at || b.created_at || 0) - (a.updated_at || a.created_at || 0);
    });
  }, [decks, searchInput, assignableFilter, sharingFilter, sortKey]);

  const activeFilterCount =
    (assignableFilter !== "all" ? 1 : 0) + (sharingFilter !== "all" ? 1 : 0);
  const hasSearchOrFilters = Boolean(searchInput || activeFilterCount);

  const clearSearchAndFilters = () => {
    setSearchInput("");
    setAssignableFilter("all");
    setSharingFilter("all");
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Delete

  const handleConfirmDelete = async (deck) => {
    try {
      await postRightworkAPI(API_delete_learning_swipe_deck, {
        company_id: homeCompany,
        learning_swipe_deck_uuid: deck.uuid,
      });
      setPendingDelete(null);
      toast.success("Swipe deck deleted", { autoClose: 2000 });
      fetchDecks();
    } catch (err) {
      // Surface the backend reason (e.g. "still used in N sections") so the
      // admin knows what to fix, instead of failing silently.
      const message =
        err?.response?.data?.system?.message || "Failed to delete swipe deck";
      toast.error(message, { autoClose: 5000 });
    }
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Render

  return (
    <div className="min-h-screen bg-white">
      {/* Header section */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">
                Swipe Decks
              </h1>
              <p className="text-sm text-slate-600">{BLURB[purpose]}</p>
            </div>
            <Tooltip title="Create new swipe deck">
              <button
                type="button"
                onClick={() => onCreate && onCreate()}
                className="bg-violet-700 hover:bg-violet-800 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
              >
                <AddIcon style={{ fontSize: "20px" }} />
                <span className="font-medium">New Deck</span>
              </button>
            </Tooltip>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-6 text-sm text-slate-600 mb-6">
            <div className="flex items-center gap-2">
              <StyleOutlinedIcon style={{ fontSize: "18px" }} />
              <span>
                {filteredDecks.length}{" "}
                {filteredDecks.length === 1 ? "Deck" : "Decks"}
              </span>
            </div>
            {searchInput && (
              <div className="flex items-center gap-2 text-violet-600">
                <SearchIcon style={{ fontSize: "16px" }} />
                <span>Showing search results</span>
              </div>
            )}
            {activeFilterCount > 0 && (
              <div className="flex items-center gap-2 text-violet-600">
                <FilterListIcon style={{ fontSize: "16px" }} />
                <span>
                  {activeFilterCount}{" "}
                  {activeFilterCount === 1 ? "filter" : "filters"} active
                </span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full max-w-md">
              <SearchIcon
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                style={{ fontSize: "20px" }}
              />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search swipe decks..."
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:border-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm"
              />
            </div>

            <Select
              size="small"
              sx={filterSelectSx}
              value={sortKey}
              onChange={(event) => setSortKey(event.target.value)}
            >
              <MenuItem value="recent" sx={{ fontSize: 14 }}>
                Recently updated
              </MenuItem>
              <MenuItem value="name" sx={{ fontSize: 14 }}>
                Name
              </MenuItem>
              <MenuItem value="cards" sx={{ fontSize: 14 }}>
                Most cards
              </MenuItem>
            </Select>
          </div>

          <div className="mt-4">
            <LibraryFilterChips
              groups={[
                // Assignability doesn't exist on Engagement decks.
                ...(isEngagement
                  ? []
                  : [
                      {
                        key: "assignable",
                        value: assignableFilter,
                        onChange: setAssignableFilter,
                        options: ASSIGNABLE_OPTIONS,
                      },
                    ]),
                ...(isEngagement
                  ? [
                      {
                        key: "sharing",
                        value: sharingFilter,
                        onChange: setSharingFilter,
                        options: SHARING_OPTIONS,
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6">
        {/* Loading state — without this, the gap between page mount and
            fetch resolution renders an empty grid that looks identical
            to a "no decks yet" empty state. */}
        {isLoading && (
          <div className="flex justify-center py-16">
            <LoadSpinner />
          </div>
        )}

        {/* Empty / no-results state */}
        {!isLoading && filteredDecks.length === 0 && (
          <div className="mt-8">
            <TrainingEmptyState
              icon={StyleOutlinedIcon}
              plural="swipe decks"
              blurb={
                isEngagement
                  ? "Share a quick update or a poll as a deck of swipeable cards. Anyone with the link can open it, no login."
                  : "Create a deck of bite-sized cards your team can swipe through. Each deck takes about 90 seconds and feels like flipping through a story."
              }
              createLabel="Create your first deck"
              searching={hasSearchOrFilters}
              noResultsTitle="No swipe decks match your filters"
              noResultsBlurb="Try adjusting your search or clearing the selected filters."
              clearLabel="Clear filters"
              onClear={hasSearchOrFilters ? clearSearchAndFilters : undefined}
              onCreate={() => onCreate && onCreate()}
            />
          </div>
        )}

        {/* Card grid — desktop-first breakpoints (this codebase uses
            max-width Tailwind breakpoints, not the default min-width). */}
        <div className="grid grid-cols-3 lg:grid-cols-2 sm:grid-cols-1 gap-6">
          {filteredDecks.map((deck) => (
            <DeckCard
              key={deck.uuid}
              deck={deck}
              isEngagement={isEngagement}
              onEdit={() => onEdit && onEdit(deck)}
              onPreview={() => onPreview && onPreview(deck)}
              onMove={() => setPendingMove(deck)}
              onDeleteRequest={() => setPendingDelete(deck)}
            />
          ))}
        </div>
      </div>

      {pendingMove && (
        <MoveAssetConfirm
          name={pendingMove.name}
          assetType="swipe_deck"
          toPurpose={isEngagement ? "training" : "engagement"}
          onCancel={() => setPendingMove(null)}
          onConfirm={() => moveDeck(pendingMove)}
        />
      )}

      {pendingDelete && (
        <DeleteConfirm
          deck={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => handleConfirmDelete(pendingDelete)}
        />
      )}
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// DeckCard

function DeckCard({ deck, isEngagement, onEdit, onPreview, onMove, onDeleteRequest }) {
  const cardsCount = deck.cards_count ?? 0;
  const estimatedSeconds = estimateDeckSeconds(cardsCount);
  const coverUrl = deck.cover?.url || null;
  const updatedLabel = deck.updated_at
    ? `Updated ${formatDate(deck.updated_at)}`
    : null;
  const openCount = deck.public_open_count || 0;

  return (
    <div
      className="group relative rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg transition-all duration-200 cursor-pointer"
      onClick={onEdit}
    >
      {/* Cover thumbnail. Always renders the violet gradient as
          backdrop — when a coverUrl exists, it doubles as a loading
          placeholder so the card doesn't flash white-then-image; when
          there's no cover, it stays as the permanent fallback with the
          deck-stack icon overlaid. */}
      <div
        className="relative h-40 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)" }}
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/70">
            <StyleOutlinedIcon style={{ fontSize: 56 }} />
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <h3 className="text-base font-semibold text-slate-900 truncate">
          {deck.name || "Untitled deck"}
        </h3>
        {deck.description && (
          <p className="mt-1 text-sm text-slate-500 line-clamp-2">
            {deck.description}
          </p>
        )}
        {/* Plain facts on one line, badges on their own: mixing pills into a
            wrapping text row left dangling separators and a stranded date once
            it broke. */}
        <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500">
          <div className="flex min-w-0 items-center gap-2 truncate">
            <span className="shrink-0">
              {cardsCount} {cardsCount === 1 ? "card" : "cards"}
            </span>
            {estimatedSeconds > 0 && (
              <>
                <span className="text-slate-300">·</span>
                <span className="shrink-0">~{formatSeconds(estimatedSeconds)}</span>
              </>
            )}
          </div>
          {updatedLabel && (
            <span className="shrink-0 text-slate-400">{updatedLabel}</span>
          )}
        </div>

        {((deck.is_directly_assignable && !isEngagement) ||
          deck.public_is_shared ||
          (isEngagement && openCount > 0)) && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            {deck.is_directly_assignable && !isEngagement && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 ring-1 ring-emerald-100">
                Assignable
              </span>
            )}
            {/* The reach count rides INSIDE the badge: it's the one number a
                manager wants off a shared deck, and it reads as a stat there
                rather than as one more item in a list of facts. Opens survive
                the link being switched off, so past reach doesn't vanish with
                it: the badge goes grey and reads "Sharing off" rather than
                disappearing and taking the count with it. */}
            {/* Training cards only flag a still-live legacy link (a nudge to
                move the deck); the Engagement card is the full badge. */}
            {(deck.public_is_shared || (isEngagement && openCount > 0)) && (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-semibold ring-1 ${
                  deck.public_is_shared
                    ? "bg-sky-50 text-sky-700 ring-sky-100"
                    : "bg-slate-100 text-slate-500 ring-slate-200"
                }`}
              >
                <LinkIcon style={{ fontSize: 14 }} />
                {deck.public_is_shared ? "Shared" : "Sharing off"}
                {openCount > 0 && (
                  <>
                    <span className={deck.public_is_shared ? "text-sky-300" : "text-slate-300"}>
                      ·
                    </span>
                    <span className="tabular-nums">
                      {openCount} {openCount === 1 ? "open" : "opens"}
                    </span>
                  </>
                )}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Hover actions */}
      <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Tooltip title="Preview">
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onPreview && onPreview();
              }}
              sx={{
                backgroundColor: "rgba(255,255,255,0.92)",
                "&:hover": { backgroundColor: "white" },
              }}
            >
              <VisibilityIcon style={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Edit">
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit && onEdit();
              }}
              sx={{
                backgroundColor: "rgba(255,255,255,0.92)",
                "&:hover": { backgroundColor: "white" },
              }}
            >
              <EditIcon style={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={isEngagement ? "Move to Training" : "Move to Engagement"}>
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onMove && onMove();
              }}
              sx={{
                backgroundColor: "rgba(255,255,255,0.92)",
                "&:hover": { backgroundColor: "white" },
              }}
            >
              <DriveFileMoveOutlinedIcon style={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete">
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRequest && onDeleteRequest();
              }}
              sx={{
                backgroundColor: "rgba(255,255,255,0.92)",
                "&:hover": { backgroundColor: "white" },
                color: "#dc2626",
              }}
            >
              <DeleteIcon style={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// DeleteConfirm

function DeleteConfirm({ deck, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Delete this swipe deck?
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          <span className="font-medium text-slate-900">
            {deck.name || "Untitled"}
          </span>{" "}
          will be permanently removed. Sections that embed it will lose the
          reference, and employee response history for this deck will be
          deleted.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            Delete deck
          </button>
        </div>
      </div>
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Helpers

/**
 * Heuristic deck-duration estimate for the dashboard subtitle. We don't
 * know per-card type from the summary endpoint, so use an average of 12s
 * per card. When the BE summary endpoint exposes per-type counts (or
 * pre-computed duration), swap this for the real number.
 */
function estimateDeckSeconds(cardCount) {
  if (!cardCount) return 0;
  return cardCount * 12;
}

function formatSeconds(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

export default SwipeDecksDisplay;
